/**
 * Commercial-licence helpers: licence-number derivation, the public
 * verification URL, and the printable certificate document.
 *
 * SIMULATED. `generateLicenseNumber` hashes the account email so the same
 * account always sees the same licence number across reloads without any
 * persistence. A real backend issues and stores licence numbers; see
 * docs/api-contract.md, "Licenses".
 *
 * The certificate builder escapes every interpolated value. The output is fed
 * to document.write() and offered as a Blob download, so unescaped account
 * fields would be an injection sink the moment any of this data arrives from a
 * server rather than from the local user.
 */
import { createElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { CommercialCertificateFields } from './commercialCertificate';
import type { Song, SongOrigin } from '../types/domain';
import CertificateDocument from '../components/license/CertificateDocument';
import { hashString } from './hash';

const DEFAULT_PUBLIC_LICENSE_ORIGIN = 'https://lyrixsa.ai';

export const GENERATED_SONG_LICENSE_COVERAGE_REASON =
  "Covered by this account's commercial licence for in-app generated songs.";

export const UPLOADED_SONG_LICENSE_DISCLAIMER =
  'Uploaded songs are not covered by the Lyrixsa Commercial License because their rights cannot be validated. They cannot be delivered through Lyrixsa Publisher.';

/**
 * Derives a stable display licence number from the account email in MOCK mode only.
 *
 * A real backend must issue and store licence numbers; this exists so the prototype's local
 * owner record remains stable until that registry exists.
 */
export function generateLicenseNumber(email: string): string {
  const h = hashString(email + 'Lyrixa-commercial');
  const padded = String(h % 1000000).padStart(6, '0');
  return `LYR-2026-${padded}`;
}

function normalizedOrigin(origin: string): string {
  return origin.replace(/\/+$/, '');
}

function currentAppOrigin(): string {
  return typeof window === 'undefined' ? DEFAULT_PUBLIC_LICENSE_ORIGIN : window.location.origin;
}

/** Public, unauthenticated verification URL for a licence number. */
export function verificationUrl(licenseNumber: string, origin?: string): string {
  return `${normalizedOrigin(origin ?? currentAppOrigin())}/license/verify/${encodeURIComponent(licenseNumber)}`;
}

export function songOriginFor(song: Pick<Song, 'songOrigin' | 'genre' | 'aiModel'>): SongOrigin {
  if (song.songOrigin === 'generated' || song.songOrigin === 'uploaded') return song.songOrigin;
  return song.genre === 'Uploaded' || song.aiModel === 'Uploaded audio' ? 'uploaded' : 'generated';
}

export function isUploadedSong(song: Pick<Song, 'songOrigin' | 'genre' | 'aiModel'>): boolean {
  return songOriginFor(song) === 'uploaded';
}

export function commercialCoverageForOwnedSong(
  song: Pick<Song, 'songOrigin' | 'genre' | 'aiModel'>
): {
  covered: boolean;
  coverageReason: string;
} {
  return isUploadedSong(song)
    ? { covered: false, coverageReason: UPLOADED_SONG_LICENSE_DISCLAIMER }
    : { covered: true, coverageReason: GENERATED_SONG_LICENSE_COVERAGE_REASON };
}

export type CertificateFields = CommercialCertificateFields;

/** Builds the full printable certificate HTML document. */
export function buildCertificateHtml(fields: CertificateFields): string {
  const documentHtml = renderToStaticMarkup(
    createElement(CertificateDocument, {
      fields,
      scale: 0.96,
    })
  );

  return `<!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="utf-8" />
        <title>Lyrixsa AI — Commercial License Certificate</title>
        <style>
          @page { margin: 0; }
          html, body {
            margin: 0;
            background: #FFFFFF;
          }
          body {
            padding: 0;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .lyx-cert-sheet {
            width: 600px;
            max-width: 600px;
            margin: 0 auto;
          }
        </style>
      </head>
      <body>
        <div class="lyx-cert-sheet">${documentHtml}</div>
      </body>
    </html>
  `;
}
